import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'react-icons-kit';
import { ic_mode_edit } from 'react-icons-kit/md/ic_mode_edit';
import { HoverTooltip } from 'shared/components/ui/HoverTooltip';
import {
  buttonClasses,
  buttonRowClasses,
  inputClasses,
} from 'styles/tailwindClassnames';
import {
  CODE_EDITOR_MIN_HEIGHT,
  HEADER_CLASSES,
  PANEL_CLASSES,
} from './styles';
import { DragPathRef } from './dragAndDrop';
import { SamplePayload } from './useSamplePayload';
import SourceTree from './SourceTree';

// The left panel: the sample payload as a tree to map from, and its JSON editor.
const SourcePayloadPanel = ({
  payload,
  readOnly,
  treeRef,
  onTreeEscape,
  dragPathRef,
  onLeafActivate,
  pickActive,
  armedPath,
  mappedTo,
}: {
  payload: SamplePayload;
  readOnly: boolean;
  treeRef: React.RefObject<HTMLDivElement>;
  onTreeEscape: (e: React.KeyboardEvent) => void;
  dragPathRef: DragPathRef;
  onLeafActivate: (path: string[]) => void;
  pickActive: boolean;
  armedPath: string[] | null;
  mappedTo: Record<string, string[]>;
}) => {
  const { t } = useTranslation();
  const { isEditingJson } = payload;
  return (
    <div className={PANEL_CLASSES}>
      <div className={HEADER_CLASSES}>
        <span>{t('Source Payload')}</span>
        {/* The panel is overflow-hidden, so the tooltip must portal out
            or it is clipped by the header. */}
        {!readOnly && !isEditingJson && (
          <HoverTooltip
            content={t('Edit sample payload')}
            position="top"
            focusable={false}
            portal
          >
            <button
              type="button"
              data-testid="JqFilterBuilder-edit-sample-button"
              aria-label={t('Edit sample payload')}
              onClick={payload.startEditing}
              className="text-greyscale-5 hover:text-ui-text-primary-text"
            >
              <Icon icon={ic_mode_edit} size={18} />
            </button>
          </HoverTooltip>
        )}
        {!readOnly && isEditingJson && (
          <div className={buttonRowClasses.row}>
            <button
              onClick={payload.cancelEditing}
              className={buttonClasses.outlineXs}
              type="button"
            >
              {t('Cancel')}
            </button>
            <button
              type="button"
              data-testid="JqFilterBuilder-save-sample-button"
              onClick={payload.applyJsonEdit}
              className={buttonClasses.outlineXs}
            >
              {t('Save')}
            </button>
          </div>
        )}
      </div>
      {isEditingJson && (
        <div className="flex flex-1 flex-col gap-2 p-2.5">
          <textarea
            data-testid="JqFilterBuilder-sample-textarea"
            value={payload.rawJsonInput}
            onChange={(e) => payload.setRawJsonInput(e.target.value)}
            className={`${inputClasses.textarea} min-h-[160px] flex-1 resize-none font-mono text-body-sm`}
          />
          {payload.jsonParseError && (
            <div className="text-body-sm text-ui-status-error-destructive">
              {payload.jsonParseError}
            </div>
          )}
        </div>
      )}
      {/* Hidden, not unmounted, while the sample is edited, so the
          "Filter fields…" text survives the editor round-trip. */}
      <div
        className={`flex-1 flex-col ${isEditingJson ? 'hidden' : 'flex'}`}
        style={{ height: CODE_EDITOR_MIN_HEIGHT }}
      >
        <SourceTree
          sampleInput={payload.sampleInput}
          readOnly={readOnly}
          treeRef={treeRef}
          onEscape={onTreeEscape}
          collapsed={payload.collapsedNodes}
          onToggle={payload.toggleCollapsed}
          dragPathRef={dragPathRef}
          onLeafActivate={onLeafActivate}
          pickActive={pickActive}
          armedPathKey={armedPath?.join('.')}
          mappedTo={mappedTo}
        />
        {(pickActive || armedPath) && (
          <div className="mt-auto flex items-center gap-1.5 border-t border-ui-divider-lines bg-greyscale-11 px-3.5 py-2 text-body-sm">
            <span className="font-normal text-ui-cta-primary">
              {pickActive
                ? t(
                    'Click or press Enter on a field to select it — Esc to cancel',
                  )
                : t('Now click a mapping slot on the right — Esc to cancel')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SourcePayloadPanel;
